import { STATUS, colorFor } from '../lib/definitions/palette';
import { barPercent, barPercentOfKnown, type BarCounts, type BarId, type BarState } from "../lib/auditTable";
import { arcPath, metricCountLine, pctToAngle, polar } from "../lib/auditMetricView";
import { useIsPhone } from "../lib/useIsPhone";

type Theme = "light" | "dark";

export interface AuditMetricItem {
  id: BarId;
  labelText: string;
  counts: BarCounts;
  tooltip: string;
  selected: BarState | null;
  /** The scope the Top-Level Region metric counts; shown once, in the count line. */
  scopeTag?: string | null;
}

interface ViewProps {
  items: AuditMetricItem[];
  onPick: (bar: BarId, state: BarState) => void;
  effectiveTheme: Theme;
}

const HATCH_ID = "audit-metric-hatch";

/** Same segments, colours and order as the bar: green, amber (Clock Set), hatched unknown, red. */
function segments(bar: BarId, c: BarCounts, theme: Theme): { state: BarState; n: number; stroke: string }[] {
  const all: { state: BarState; n: number; stroke: string }[] = [
    { state: "yes", n: c.yes, stroke: colorFor(STATUS.good, theme) },
    ...(bar === "clock" ? [{ state: "mid" as BarState, n: c.mid, stroke: colorFor(STATUS.warning, theme) }] : []),
    { state: "unknown", n: c.unknown, stroke: `url(#${HATCH_ID})` },
    { state: "no", n: c.no, stroke: colorFor(STATUS.critical, theme) },
  ];
  return all.filter((s) => s.n > 0);
}

function HatchDefs() {
  return (
    <defs>
      <pattern id={HATCH_ID} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="var(--gridline)" />
        <line x1="0" y1="0" x2="0" y2="6" stroke="var(--ink-muted)" strokeWidth="2" />
      </pattern>
    </defs>
  );
}

function MetricHead({ item }: { item: AuditMetricItem }) {
  return <div className="audit-metric-label" title={item.tooltip}>{item.labelText}</div>;
}

function ringsOrGauge({ item, onPick, effectiveTheme, gauge }: { item: AuditMetricItem; onPick: ViewProps["onPick"]; effectiveTheme: Theme; gauge: boolean }) {
  const c = item.counts;
  const pct = barPercent(c);
  const segs = segments(item.id, c, effectiveTheme);
  const sweep = gauge ? 180 : 360;
  const start = gauge ? -90 : 0;
  const cx = gauge ? 75 : 60;
  const cy = gauge ? 78 : 60;
  const r = gauge ? 58 : 46;
  const sw = gauge ? 14 : 14;
  let cum = 0;
  const arcs = segs.map((s) => {
    const a0 = start + (cum * sweep) / Math.max(1, c.total);
    cum += s.n;
    const a1 = start + (cum * sweep) / Math.max(1, c.total);
    return (
      <path
        key={s.state}
        d={arcPath(cx, cy, r, a0, a1)}
        fill="none"
        stroke={s.stroke}
        strokeWidth={sw}
        className={`audit-metric-arc${item.selected === s.state ? " audit-metric-arc-selected" : ""}${item.selected !== null && item.selected !== s.state ? " audit-metric-arc-dim" : ""}`}
        role="button"
        tabIndex={0}
        aria-label={`${item.labelText} ${s.state} ${s.n}`}
        aria-pressed={item.selected === s.state}
        onClick={() => onPick(item.id, s.state)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(item.id, s.state); } }}
      />
    );
  });
  const track = gauge ? arcPath(cx, cy, r, -90, 90) : arcPath(cx, cy, r, 0, 359.99);
  let needle = null;
  if (gauge) {
    const tip = polar(cx, cy, r - 20, pctToAngle(pct));
    needle = (
      <>
        <line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="var(--ink)" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={4} fill="var(--ink)" />
      </>
    );
  }
  return (
    <div className={`audit-metric audit-metric-${gauge ? "gauge" : "ring"}`} key={item.id}>
      <MetricHead item={item} />
      <svg viewBox={gauge ? "0 0 150 104" : "0 0 120 120"} width={gauge ? 150 : 120} height={gauge ? 104 : 120} role="group" aria-label={`${item.labelText} ${pct}%`}>
        <HatchDefs />
        <path d={track} fill="none" stroke="var(--border)" strokeWidth={sw} />
        {arcs}
        {needle}
        {gauge ? (
          <text x={cx} y={cy + 24} textAnchor="middle" className="audit-metric-pct">{pct}%</text>
        ) : (
          <text x={cx} y={cy + 7} textAnchor="middle" className="audit-metric-pct">{pct}%</text>
        )}
      </svg>
      <div className="settings-field-desc audit-metric-count">
        {item.scopeTag ? `${item.scopeTag} \u00b7 ` : ""}
        {metricCountLine(c.yes, c.total, c.unknown, item.id === "clock" ? barPercentOfKnown(c) : undefined)}
      </div>
    </div>
  );
}

export function AuditRings({ items, onPick, effectiveTheme }: ViewProps) {
  const isPhone = useIsPhone();
  return (
    <div className="audit-metric-row" data-phone={isPhone}>
      {items.map((item) => ringsOrGauge({ item, onPick, effectiveTheme, gauge: false }))}
    </div>
  );
}

export function AuditGauges({ items, onPick, effectiveTheme }: ViewProps) {
  const isPhone = useIsPhone();
  return (
    <div className="audit-metric-row" data-phone={isPhone}>
      {items.map((item) => ringsOrGauge({ item, onPick, effectiveTheme, gauge: true }))}
    </div>
  );
}
